/**
 * 检验任务执行界面（v0.85 / P4）。
 *
 * 沈哥 2026-10-22 的核心需求：
 *   「同款产品抽样多件时，用户可能一个样件各尺寸挨个测量，
 *    也有可能同个尺寸挨个样件展开测量」
 *   「人工测量时对话框在哪个检测项，气泡图就定位到哪里」
 *
 * ⇒ 两种推进顺序（`by_item` 按件 / `by_sample` 按尺寸），配一个并排的气泡图。
 *
 * 三条不能破的口径（后端已钉，前端只做**提前提示**，不做第二套判定）
 * -------------------------------------------------------------------
 * 1. **判定是算出来的，不是填出来的**：尺寸项的合格与否由后端按公差算，
 *    界面只显示结果。前端这份 `judge()` 仅用于「超差要先填备注」的即时提示，
 *    真正的判定以后端返回为准 —— 两处不一致时以后端为准（它不是权威）。
 * 2. **超差必须填备注**（沈哥拍板）：超差不写原因，事后没人能解释。
 *    界面在输入超差值时就把备注框标红，别等点了保存才报错。
 * 3. **抽样数不能改**：格子列数 = 任务头的 `sample_qty`，建单时定死。
 *
 * 游标是**二维**的 `(seq, sample)`：只存项不存件的话，
 * 「按尺寸测到项 A 的件 2」续做时会跳回件 1（迁移 0032 加 `current_sample`）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchInspectionTask, fetchTaskCanvas, finishInspectionTask,
  measureTaskItem, type InspectionTaskRow,
} from '../api/client';
import { Canvas } from './Canvas';
import { useUI } from '../store/ui';
import type { Feature } from '../types';

type Toast = (m: string, t?: 'info' | 'success' | 'error') => void;
type Mode = 'by_item' | 'by_sample';

const MODE_KEY = 'gdt_balloon_inspect_mode';

function loadMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'by_sample' ? 'by_sample' : 'by_item';
  } catch { return 'by_item'; }
}

interface Cur { seq: number; sample: number; }

/** 这一格测了没有（**只认 samples**，不能看 item.verdict —— 那是整项聚合）。 */
function hasSample(it: any, sn: number): boolean {
  return (it.samples ?? []).some((s: any) => Number(s.no) === sn);
}

/** 前端即时判定（**只用于提示**，权威在后端）。 */
function judge(it: any, v: number | null): 'pass' | 'fail' | 'na' {
  if (v == null || it.nominal == null) return 'na';
  if (it.up == null && it.low == null) return 'pass';
  const hi = Number(it.nominal) + Number(it.up ?? 0);
  const lo = Number(it.nominal) + Number(it.low ?? 0);
  return v >= lo - 1e-9 && v <= hi + 1e-9 ? 'pass' : 'fail';
}

export function InspectionRunner({ taskNo, canEdit, onToast, onBack }: {
  taskNo: string;
  canEdit: boolean;
  onToast: Toast;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const { selectFeature } = useUI();
  const [mode, setMode] = useState<Mode>(loadMode);
  const [cur, setCur] = useState<Cur | null>(null);
  const [val, setVal] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** 左栏（当前一格）与底部矩阵都能收起 —— 车间要的是「看图够大」，
   *  一次测量只关心一个数，左栏收起来时气泡图能占到接近全宽。 */
  const [leftOpen, setLeftOpen] = useState(true);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const taskQ = useQuery({
    queryKey: ['inspection-task', taskNo],
    queryFn: () => fetchInspectionTask(taskNo),
    refetchOnWindowFocus: false,
  });
  const canvasQ = useQuery({
    queryKey: ['task-canvas', taskNo],
    queryFn: () => fetchTaskCanvas(taskNo),
    refetchOnWindowFocus: false,
  });
  const task = taskQ.data;
  const items = useMemo<any[]>(() => task?.items ?? [], [task]);
  const nSample = Math.max(1, Number(task?.sample_count ?? 1));

  /** 二维游标初始化：**从服务端断点续做**，不要每次进来都回第一格。 */
  useEffect(() => {
    if (!task || cur) return;
    const seq = Math.min(Math.max(0, task.current_seq ?? 0), Math.max(0, items.length - 1));
    const sn = Math.min(Math.max(0, (task as any).current_sample ?? 0), nSample - 1);
    setCur({ seq, sample: sn });
  }, [task, cur, items.length, nSample]);

  const it = cur ? items[cur.seq] : null;

  /** 换焦点 ⇒ 气泡跟着定位（**只跟项走，与件号无关**：同一尺寸不同件看的是同一个位置）。 */
  useEffect(() => {
    if (!it || !canvasQ.data?.drawing_id) return;
    const feats: any[] = canvasQ.data.features ?? [];
    const fid = it.feature_id
      ? (feats.find((f) => String(f.feature_id) === String(it.feature_id))?.id
         ?? feats.find((f) => String(f.id) === String(it.feature_id))?.id)
      : undefined;
    const target = fid ?? (it.balloon_no != null
      ? feats.find((f) => Number(f.balloon_no) === Number(it.balloon_no))?.id
      : undefined);
    if (target != null) selectFeature(Number(target));
  }, [it, canvasQ.data, selectFeature]);

  useEffect(() => {
    // 换格时清空输入并把焦点放回输入框 —— 键盘流是车间唯一的快路径
    setVal('');
    setNote('');
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [cur?.seq, cur?.sample]);

  const setModePersist = useCallback((m: Mode) => {
    setMode(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* 隐私模式忽略 */ }
  }, []);

  /** 按当前模式找**下一个还没测**的格子；全测完 → null。 */
  const nextCursor = useCallback((from: Cur, t: InspectionTaskRow): Cur | null => {
    const list: any[] = t.items ?? [];
    const n = Math.max(1, Number((t as any).sample_count ?? 1));
    const order: Cur[] = [];
    if (mode === 'by_item') {
      for (let s = 0; s < n; s += 1) {
        for (let i = 0; i < list.length; i += 1) order.push({ seq: i, sample: s });
      }
    } else {
      for (let i = 0; i < list.length; i += 1) {
        for (let s = 0; s < n; s += 1) order.push({ seq: i, sample: s });
      }
    }
    const at = order.findIndex((c) => c.seq === from.seq && c.sample === from.sample);
    for (let k = at + 1; k < order.length; k += 1) {
      const c = order[k];
      if (!hasSample(list[c.seq], c.sample)) return c;
    }
    for (let k = 0; k <= at && k < order.length; k += 1) {
      const c = order[k];
      if (!hasSample(list[c.seq], c.sample)) return c;
    }
    return null;
  }, [mode]);

  const doSave = useCallback(async () => {
    if (!cur || !it) return;
    const v = it.nominal != null ? Number(val) : null;
    if (it.nominal != null && (val.trim() === '' || Number.isNaN(v))) {
      onToast('请填实测值', 'error');
      inputRef.current?.focus();
      return;
    }
    // 前端先拦一次超差无备注（后端也会拦，这里只是省一次往返）
    if (it.nominal != null && judge(it, v) === 'fail' && !note.trim()) {
      onToast(`超差（标准 ${it.nominal}）—— 请先填原因/处置备注`, 'error');
      return;
    }
    setBusy(true);
    try {
      const t = await measureTaskItem(taskNo, {
        seq: cur.seq, sample_no: cur.sample,
        value: v, note: note.trim(),
        verdict: it.nominal == null ? 'pass' : null,
      });
      qc.setQueryData(['inspection-task', taskNo], t);
      const nxt = nextCursor(cur, t);
      if (nxt) setCur(nxt);
      else onToast('这一张任务的所有格子都测完了 —— 点「完成」收尾', 'success');
    } catch (e: any) {
      onToast(e.message, 'error');
    } finally { setBusy(false); }
  }, [cur, it, val, note, taskNo, qc, nextCursor, onToast]);

  const doFinish = useCallback(async () => {
    if (!window.confirm('确认收尾？收尾后实测值不能再改。')) return;
    setBusy(true);
    try {
      const t = await finishInspectionTask(taskNo);
      qc.setQueryData(['inspection-task', taskNo], t);
      onToast(`任务已完成，结论：${t.result === 'fail' ? '不合格' : '合格'}`,
              t.result === 'fail' ? 'error' : 'success');
    } catch (e: any) {
      onToast(e.message, 'error');
    } finally { setBusy(false); }
  }, [taskNo, qc, onToast]);

  /* ---------------------------------------------------------------- 渲染 */
  if (taskQ.isLoading) return <div className="sip-state">正在读取任务…</div>;
  if (taskQ.isError) {
    return (
      <div className="sip-state">
        <div className="sip-state-title">任务打不开</div>
        <div className="sip-state-desc">{(taskQ.error as any)?.message}</div>
      </div>
    );
  }
  if (!task) return null;

  const done = items.reduce(
    (n, x) => n + (x.samples ?? []).length, 0);
  const totalCells = items.length * nSample;
  const locked = !canEdit || ['done', 'closed', 'abandoned'].includes(task.status);

  return (
    /* ★ 全屏专注模式（fixed inset:0）。
       原先是普通页面流：被 App 顶栏 + 内容区 padding 吃掉一截，
       1920 屏上留给图纸的宽度只剩一半，看尺寸得反复缩放 ——
       而执行任务时导航根本用不上（返回在本页顶栏），所以整屏让给图纸。 */
    <div style={{
      // z-index 取 900：**压住** App 顶栏（sticky，z-index 个位数），
      // 但**低于**模态/右键菜单（1000 / 1200）—— toast 与确认框仍能盖在上面，
      // 否则「超差要填备注」这类提示会被全屏界面吃掉。
      position: 'fixed', inset: 0, zIndex: 900, background: '#fff',
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      {/* ---------------- 顶栏 ---------------- */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                    padding: '8px 12px', borderBottom: '1px solid #e5e7eb',
                    flexWrap: 'wrap' }}>
        <button className="btn sm" onClick={onBack}>‹ 返回</button>
        <b style={{ fontFamily: 'monospace' }}>{task.task_no}</b>
        <span style={{ fontSize: 12, opacity: 0.7 }}>
          {task.task_type} · {task.material_code || '—'}
          {task.lot_no ? ` · 批次 ${task.lot_no}` : ''}
          {' · 抽样 '}{nSample}{' 件'}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, opacity: 0.8 }}>
          {done}/{totalCells} 格
        </span>
        <div style={{ display: 'inline-flex', border: '1px solid #d0d7de',
                      borderRadius: 6, overflow: 'hidden' }}>
          {([['by_item', '按件测'], ['by_sample', '按尺寸测']] as const).map(([m, label]) => (
            <button key={m} onClick={() => setModePersist(m)}
                    title={m === 'by_item'
                      ? '一件件的各尺寸挨着测（卡尺、高度规）'
                      : '一个尺寸把所有件测完（千分尺、通止规、专用检具）'}
                    style={{
                      padding: '4px 10px', fontSize: 12, border: 'none',
                      cursor: 'pointer',
                      background: mode === m ? '#1f6feb' : '#fff',
                      color: mode === m ? '#fff' : '#333',
                    }}>
              {label}
            </button>
          ))}
        </div>
        <button className="btn sm" onClick={() => setLeftOpen((v) => !v)}
                title={leftOpen ? '收起左栏，让图纸占满整屏' : '展开左栏继续录入'}>
          {leftOpen ? '只看图' : '打开录入'}
        </button>
        <button className="btn sm" onClick={() => setMatrixOpen((v) => !v)}
                title="展开/收起「测到哪了」矩阵">
          {matrixOpen ? '收起矩阵' : '看矩阵'}
        </button>
        {canEdit && !locked && (
          <button className="btn sm primary" disabled={busy} onClick={doFinish}>
            完成
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ---------------- 左：当前一格（可收起） ---------------- */}
        {leftOpen && (
        <div style={{ width: 'min(420px, 36vw)', minWidth: 300,
                      borderRight: '1px solid #e5e7eb',
                      padding: 12, overflow: 'auto', flexShrink: 0 }}>
          {!it && <div className="sip-state">这一张任务没有检验项</div>}
          {it && cur && (
            <>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                第 {cur.seq + 1} / {items.length} 项 · 样件 {cur.sample + 1} / {nSample}
              </div>
              <div style={{ fontSize: 17, fontWeight: 600, margin: '4px 0 2px' }}>
                {it.proj || '(无项目名)'}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                气泡 {it.balloon_no ?? '—'}
                {it.cc_sc ? ` · ${it.cc_sc}` : ''}
                {it.method ? ` · ${it.method}` : ''}
              </div>

              <div style={{ background: '#f6f8fa', borderRadius: 8,
                            padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, opacity: 0.7 }}>标准值 / 公差</div>
                <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'monospace' }}>
                  {it.nominal ?? '—'}
                  {it.nominal != null && (it.up != null || it.low != null) && (
                    <span style={{ fontSize: 13, marginLeft: 8, opacity: 0.75 }}>
                      {it.up != null ? `+${it.up}` : ''}{it.low != null ? ` / ${it.low}` : ''}
                    </span>
                  )}
                </div>
              </div>

              <input
                ref={inputRef}
                disabled={locked || busy}
                value={val}
                inputMode="decimal"
                placeholder={it.nominal != null ? '输入实测值，回车保存' : '文本项：可留空后回车'}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void doSave(); }
                }}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 26,
                  padding: '10px 12px', fontFamily: 'monospace',
                  border: `2px solid ${
                    (() => {
                      const v = val.trim() === '' ? null : Number(val);
                      if (v != null && judge(it, v) === 'fail') return '#d1242f';
                      return '#d0d7de';
                    })()}`,
                  borderRadius: 8, marginBottom: 8,
                }}
              />

              <input
                value={note}
                disabled={locked || busy}
                placeholder={(() => {
                  const v = val.trim() === '' ? null : Number(val);
                  return v != null && judge(it, v) === 'fail'
                    ? '超差必填：原因 / 处置' : '备注（可选）';
                })()}
                onChange={(e) => setNote(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 14,
                  padding: '8px 10px', marginBottom: 10,
                  border: `1px solid ${
                    (() => {
                      const v = val.trim() === '' ? null : Number(val);
                      return v != null && judge(it, v) === 'fail' ? '#d1242f' : '#d0d7de';
                    })()}`,
                  borderRadius: 8,
                }}
              />

              <button className="btn primary" disabled={locked || busy}
                      onClick={doSave} style={{ width: '100%' }}>
                {busy ? '保存中…' : '保存并下一格（Enter）'}
              </button>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                  这一项的实测记录
                </div>
                {(it.samples ?? []).length === 0 && (
                  <div style={{ fontSize: 12, opacity: 0.5 }}>还没测</div>
                )}
                {(it.samples ?? []).map((s: any) => (
                  <div key={s.no} style={{ fontSize: 13, padding: '3px 0',
                                           display: 'flex', gap: 6 }}>
                    <span style={{ opacity: 0.6 }}>件{Number(s.no) + 1}</span>
                    <b style={{ fontFamily: 'monospace' }}>{s.v ?? '—'}</b>
                    <span style={{ color: s.verdict === 'fail' ? '#d1242f' : '#1a7f37' }}>
                      {s.verdict === 'fail' ? '超差' : 'OK'}
                    </span>
                    {s.note && <span style={{ opacity: 0.65 }}>{s.note}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        )}

        {/* ---------------- 右：气泡图 ---------------- */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {canvasQ.data?.drawing_id ? (
            <Canvas
              drawingId={canvasQ.data.drawing_id}
              widthPt={canvasQ.data.width_pt}
              heightPt={canvasQ.data.height_pt}
              pageIndex={canvasQ.data.page_index}
              features={(canvasQ.data.features ?? []) as unknown as Feature[]}
              readOnly
            />
          ) : (
            <div className="sip-state">
              <div className="sip-state-title">这张任务没有关联图纸</div>
              <div className="sip-state-desc">
                明细是建单时的快照，任务照样可以做；只是没有气泡图可定位。
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- 底部：矩阵进度（默认收起，只留一条细进度）
          矩阵有 150px 高，收起后这部分高度全给图纸 —— 录入时看的是
          「当前这一格」，不是整张矩阵。 */}
      <div style={{ borderTop: '1px solid #e5e7eb', flexShrink: 0,
                    padding: matrixOpen ? '6px 12px' : '4px 12px',
                    maxHeight: matrixOpen ? 160 : 26, overflow: 'auto' }}>
        {!matrixOpen && (
          <div title={`已测 ${done} / ${totalCells} 格（点右上「看矩阵」展开）`}
               style={{ height: 8, background: '#eef2f7', borderRadius: 4,
                        overflow: 'hidden' }}>
            <div style={{ width: `${totalCells ? Math.round(100 * done / totalCells) : 0}%`,
                          height: '100%', background: '#1f6feb' }} />
          </div>
        )}
        {matrixOpen && (
        <>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
          测到哪了（点格子直接跳过去）
        </div>
        {items.map((x, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3,
                                marginBottom: 2 }}>
            <span style={{ width: 150, fontSize: 11, overflow: 'hidden',
                           whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                           opacity: 0.8 }}
                  title={x.proj}>
              {i + 1}. {x.proj}
            </span>
            {Array.from({ length: nSample }).map((_, s) => {
              const smp = (x.samples ?? []).find((z: any) => Number(z.no) === s);
              const on = cur?.seq === i && cur?.sample === s;
              const bg = smp
                ? (smp.verdict === 'fail' ? '#f7c1c1' : '#c0dd97')
                : '#eef2f7';
              return (
                <button key={s} onClick={() => setCur({ seq: i, sample: s })}
                        title={`项${i + 1} 件${s + 1}${smp ? `：${smp.v}` : '：未测'}`}
                        style={{
                          width: 22, height: 18, fontSize: 10, padding: 0,
                          background: bg, cursor: 'pointer',
                          border: on ? '2px solid #1f6feb' : '1px solid #d0d7de',
                          borderRadius: 3,
                        }} />
              );
            })}
          </div>
        ))}
        </>
        )}
      </div>
    </div>
  );
}
